import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { useConsultation } from "@/components/ConsultationProvider";
import { useContent } from "@/context/ContentProvider";
import { PROCESS } from "@/data/site";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { open } = useConsultation();
  const { services } = useContent();
  const index = services.findIndex((s) => s.slug === slug);
  const service = services[index];

  if (!service) return <Navigate to="/services" replace />;

  const next = services[(index + 1) % services.length];

  return (
    <div className="pt-16">
      <Seo
        title={`${service.name} in Cleveland, OH | Crownvera Epoxy`}
        description={`${service.tagline} Crownvera Epoxy installs ${service.name.toLowerCase()} across Cleveland & Northeast Ohio. Free in-home consultation: 440-381-7828.`}
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-14 pb-20" data-testid="service-detail">
        <Reveal>
          <Link to="/services" data-testid="service-detail-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-stone-500 hover:text-amber-700 transition-colors duration-300 mb-10">
            <ArrowLeft className="w-4 h-4" /> All services
          </Link>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-700 mb-4">Crownvera System</p>
              <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-stone-900 leading-tight">{service.name}</h1>
              <p className="mt-4 font-display text-lg italic text-amber-800">{service.tagline}</p>
              <p className="mt-6 text-base text-stone-600 leading-relaxed">{service.description}</p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                {(service.benefits || []).map((b) => (
                  <li key={b} data-testid={`service-benefit-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-center gap-3 text-sm text-stone-700">
                    <Check className="w-4 h-4 text-amber-700 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <button
                onClick={open}
                data-testid="service-detail-cta-btn"
                className="mt-10 bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-sm px-8 py-4 rounded-md shadow-md transition-colors duration-300"
              >
                Get a Free Quote
              </button>
            </div>
            <div className="lg:sticky lg:top-24">
              <div className="rounded-xl overflow-hidden border border-amber-600/25 shadow-[0_30px_60px_-30px_rgba(184,134,11,0.35)]">
                <img src={service.image} alt={`${service.name} by Crownvera Epoxy in Cleveland, Ohio`} className="w-full h-[320px] sm:h-[440px] object-cover" />
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="border-t border-amber-600/15 bg-[#F5F2EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-stone-900">How it's installed</h2>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.08}>
                <div className="gold-ring-card rounded-lg p-6 h-full">
                  <span className="font-display text-2xl text-gold-shimmer">{p.step}</span>
                  <h3 className="font-display text-base text-amber-900 mt-3 mb-2">{p.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} className="mt-14">
            <Link
              to={`/services/${next.slug}`}
              data-testid="service-detail-next"
              className="group flex items-center justify-between bg-white border border-amber-600/20 hover:border-amber-600/50 rounded-lg p-6 shadow-sm transition-colors duration-300"
            >
              <div>
                <p className="text-xs uppercase tracking-wider text-stone-500">Next system</p>
                <p className="font-display text-xl text-amber-900 mt-1">{next.name}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-amber-700 transition-transform duration-300 group-hover:translate-x-2" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
